import { Observable, Subject } from 'rxjs';
import { Time } from 'lightweight-charts';
import { IndicatorType } from '@shared/types';
import {
  MacdIndicatorData,
  LiquidationIndicatorData,
  MarketStructureIndicatorData,
  MasterPatternIndicatorData,
  MaIndicatorData,
  VwapIndicatorData,
  OrderBlocksIndicatorData,
  RsiIndicatorData,
  ZigZagIndicatorData
} from '@server/data-feed/schemas/output.schema';

// Single stream for indicator initialization (snapshot) and incremental updates (delta).
export type IndicatorUpdateType = 'snapshot' | 'delta';

type ReplaceTime<T> = T extends (infer U)[]
  ? ReplaceTime<U>[]
  : T extends object
    ? {
        [K in keyof T]: K extends 'time' ? Time : ReplaceTime<T[K]>;
      }
    : T;

type IndicatorUpdateKey = `${number}:${IndicatorType}:${number}`;

export type IndicatorUpdateStream = {
  getIndicatorUpdate$: () => Observable<IndicatorUpdate>;
  getIndicatorSnapshots: (
    timeframeIndex: number,
    indicatorType: IndicatorType
  ) => IndicatorUpdate[];
  emitIndicatorUpdate: (update: IndicatorUpdate) => void;
  clearIndicatorSnapshots: () => void;
  dispose: () => void;
};

export type IndicatorUpdate<T = unknown> = {
  timeframeIndex: number;
  indicatorIndex: number;
  indicatorType: IndicatorType;
  updateType: IndicatorUpdateType;
  data: T[];
};

export type MaIndicatorUpdate = IndicatorUpdate<ReplaceTime<MaIndicatorData>>;

export type MaIndicatorUpdatePoint = ReplaceTime<MaIndicatorData>;

export type VwapIndicatorUpdatePoint = ReplaceTime<VwapIndicatorData>;

export type VwapIndicatorUpdate = IndicatorUpdate<VwapIndicatorUpdatePoint>;

export type RsiIndicatorUpdatePoint = ReplaceTime<RsiIndicatorData>;

export type RsiIndicatorUpdate = IndicatorUpdate<RsiIndicatorUpdatePoint>;

export type MacdIndicatorUpdatePoint = ReplaceTime<MacdIndicatorData>;

export type MacdIndicatorUpdate = IndicatorUpdate<MacdIndicatorUpdatePoint>;

export type LiquidationIndicatorUpdatePoint =
  ReplaceTime<LiquidationIndicatorData>;

export type LiquidationIndicatorUpdate =
  IndicatorUpdate<LiquidationIndicatorUpdatePoint>;

export type ZigZagIndicatorUpdatePoint = ReplaceTime<ZigZagIndicatorData>;

export type ZigZagIndicatorUpdate = IndicatorUpdate<ZigZagIndicatorUpdatePoint>;

export type MasterPatternIndicatorUpdatePoint = MasterPatternIndicatorData;

export type MasterPatternIndicatorUpdate =
  IndicatorUpdate<MasterPatternIndicatorUpdatePoint>;

export type OrderBlocksIndicatorUpdatePoint =
  ReplaceTime<OrderBlocksIndicatorData>;

export type OrderBlocksIndicatorUpdate =
  IndicatorUpdate<OrderBlocksIndicatorUpdatePoint>;

export type MarketStructureIndicatorUpdatePoint =
  ReplaceTime<MarketStructureIndicatorData>;

export type MarketStructureIndicatorUpdate =
  IndicatorUpdate<MarketStructureIndicatorUpdatePoint>;

export const isMaIndicatorUpdate = (
  update: IndicatorUpdate
): update is MaIndicatorUpdate => update.indicatorType === IndicatorType.MA;

export const isVwapIndicatorUpdate = (
  update: IndicatorUpdate
): update is VwapIndicatorUpdate => update.indicatorType === IndicatorType.VWAP;

export const isRsiIndicatorUpdate = (
  update: IndicatorUpdate
): update is RsiIndicatorUpdate => update.indicatorType === IndicatorType.RSI;

export const isMacdIndicatorUpdate = (
  update: IndicatorUpdate
): update is MacdIndicatorUpdate => update.indicatorType === IndicatorType.MACD;

export const isLiquidationIndicatorUpdate = (
  update: IndicatorUpdate
): update is LiquidationIndicatorUpdate =>
  update.indicatorType === IndicatorType.LIQUIDATION;

export const isZigZagIndicatorUpdate = (
  update: IndicatorUpdate
): update is ZigZagIndicatorUpdate =>
  update.indicatorType === IndicatorType.ZIG_ZAG;

export const isMasterPatternIndicatorUpdate = (
  update: IndicatorUpdate
): update is MasterPatternIndicatorUpdate =>
  update.indicatorType === IndicatorType.MASTER_PATTERN;

export const isOrderBlocksIndicatorUpdate = (
  update: IndicatorUpdate
): update is OrderBlocksIndicatorUpdate =>
  update.indicatorType === IndicatorType.ORDER_BLOCKS;

export const isMarketStructureIndicatorUpdate = (
  update: IndicatorUpdate
): update is MarketStructureIndicatorUpdate =>
  update.indicatorType === IndicatorType.MARKET_STRUCTURE;

const buildUpdateKey = (update: {
  timeframeIndex: number;
  indicatorType: IndicatorType;
  indicatorIndex: number;
}): IndicatorUpdateKey =>
  `${update.timeframeIndex}:${update.indicatorType}:${update.indicatorIndex}`;

export const createIndicatorUpdateStream = (): IndicatorUpdateStream => {
  // Subject keeps the stream lightweight; snapshots provide proper initialization.
  const indicatorUpdate$ = new Subject<IndicatorUpdate>();
  const snapshotCache = new Map<IndicatorUpdateKey, IndicatorUpdate>();

  const getIndicatorUpdate$ = () => indicatorUpdate$.asObservable();

  const getIndicatorSnapshots = (
    timeframeIndex: number,
    indicatorType: IndicatorType
  ): IndicatorUpdate[] =>
    Array.from(snapshotCache.values())
      .filter(
        (snapshot) =>
          snapshot.timeframeIndex === timeframeIndex &&
          snapshot.indicatorType === indicatorType
      )
      .sort((left, right) => left.indicatorIndex - right.indicatorIndex);

  const emitIndicatorUpdate = (update: IndicatorUpdate) => {
    if (update.updateType === 'snapshot') {
      snapshotCache.set(buildUpdateKey(update), update);
    }
    indicatorUpdate$.next(update);
  };

  const clearIndicatorSnapshots = () => {
    snapshotCache.clear();
  };

  const dispose = () => {
    snapshotCache.clear();
    indicatorUpdate$.complete();
  };

  return {
    getIndicatorUpdate$,
    getIndicatorSnapshots,
    emitIndicatorUpdate,
    clearIndicatorSnapshots,
    dispose
  };
};
